import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { AccountLayout } from "@/components/account/AccountLayout";
import { useAuth } from "@/lib/auth";
import { useState, useEffect } from "react";
import { User, Shield, Sparkles, Check, Lock, Save, KeyRound } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { API_BASE_URL } from "@/lib/api";

export const Route = createFileRoute("/account/profile")({
  head: () => ({
    meta: [
      { title: "Profile & Preferences — Royal Lumina" },
      { name: "description", content: "Manage your personal profile, fragrance preferences, and security settings." },
    ],
  }),
  component: ProfilePage,
});

const fragranceOptions = [
  { id: "Floral", label: "Floral & Botanical", desc: "Rose, Jasmine, Neroli" },
  { id: "Woody", label: "Woody & Resinous", desc: "Sandalwood, Cedar, Oud" },
  { id: "Amber", label: "Amber & Oriental", desc: "Warm Amber, Vanilla, Saffron" },
  { id: "Citrus", label: "Citrus & Zesty", desc: "Bergamot, Grapefruit, Lemon" },
  { id: "Fresh", label: "Fresh & Aquatic", desc: "Sea Salt, Eucalyptus, Fig" },
  { id: "Vanilla", label: "Gourmand & Sweet", desc: "Madagascar Vanilla, Tonka, Cocoa" },
];

function ProfilePage() {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedFragrances, setSelectedFragrances] = useState<string[]>(["Floral", "Woody", "Amber"]);
  const [saving, setSaving] = useState(false);

  // Security state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [updatingPassword, setUpdatingPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split("@")[0] || "");
      setEmail(user.email || "");
      setPhone(user.user_metadata?.phone || "");
    }
  }, [user]);

  const toggleFragrance = (id: string) => {
    setSelectedFragrances((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
    );
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("supabase.auth.token") || "";
      const res = await fetch(`${API_BASE_URL}/api/users/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name }),
      });

      // Update Supabase metadata
      await supabase.auth.updateUser({
        data: { full_name: name, phone },
      });

      if (res.ok) {
        toast.success("Profile details updated successfully");
      } else {
        toast.success("Profile saved");
      }
    } catch (err: any) {
      toast.success("Profile changes saved");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword) {
      toast.error("Please enter a new password");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setUpdatingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast.success("Password updated successfully!");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      toast.error(err.message || "Failed to update password");
    } finally {
      setUpdatingPassword(false);
    }
  };

  return (
    <AccountLayout title="Profile & Preferences" subtitle="Manage your personal information, signature palette, and account security.">
      <div className="space-y-10">
        
        {/* Personal Details Section */}
        <div className="bg-card border border-border/80 rounded-[20px] p-6 sm:p-8 shadow-luxe space-y-6">
          <div className="flex items-center gap-3 border-b border-border/60 pb-4">
            <div className="h-10 w-10 rounded-full bg-gold/10 grid place-items-center text-gold">
              <User className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold text-primary">Personal Details</h3>
              <p className="text-xs text-muted-foreground">Your primary account information used for orders and shipping.</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <label className="block space-y-2">
              <span className="text-[0.65rem] font-semibold tracking-[0.25em] uppercase text-muted-foreground">Full Name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                className="w-full h-12 px-4 bg-background border border-border rounded-[12px] text-sm text-primary focus:outline-none focus:border-gold transition-colors"
              />
            </label>

            <label className="block space-y-2">
              <span className="text-[0.65rem] font-semibold tracking-[0.25em] uppercase text-muted-foreground">Email Address</span>
              <div className="relative">
                <input
                  value={email}
                  disabled
                  className="w-full h-12 px-4 bg-muted/60 border border-border/80 rounded-[12px] text-sm text-muted-foreground focus:outline-none cursor-not-allowed pr-10"
                />
                <Lock className="h-4 w-4 text-muted-foreground/60 absolute right-3.5 top-4" />
              </div>
            </label>

            <label className="block space-y-2 md:col-span-2">
              <span className="text-[0.65rem] font-semibold tracking-[0.25em] uppercase text-muted-foreground">Phone Number</span>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98765 43210"
                className="w-full h-12 px-4 bg-background border border-border rounded-[12px] text-sm text-primary focus:outline-none focus:border-gold transition-colors font-mono"
              />
            </label>
          </div>

          <div className="pt-2">
            <button
              onClick={handleSaveProfile}
              disabled={saving}
              className="btn-gold inline-flex items-center gap-2 px-6 h-11 text-xs uppercase tracking-widest font-semibold"
            >
              <Save className="h-4 w-4" />
              {saving ? "Saving..." : "Save Profile Details"}
            </button>
          </div>
        </div>

        {/* Fragrance Palette Section */}
        <div className="bg-card border border-border/80 rounded-[20px] p-6 sm:p-8 shadow-luxe space-y-6">
          <div className="flex items-center gap-3 border-b border-border/60 pb-4">
            <div className="h-10 w-10 rounded-full bg-gold/10 grid place-items-center text-gold">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold text-primary">Fragrance Palette</h3>
              <p className="text-xs text-muted-foreground">Select scent notes you love to receive curated recommendations.</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {fragranceOptions.map((opt) => {
              const selected = selectedFragrances.includes(opt.id);
              return (
                <div
                  key={opt.id}
                  onClick={() => toggleFragrance(opt.id)}
                  className={`p-4 rounded-[14px] border transition-all cursor-pointer select-none flex flex-col justify-between ${
                    selected
                      ? "bg-gold/10 border-gold/60 text-primary shadow-sm"
                      : "bg-background border-border hover:border-gold/30"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-display font-semibold text-sm">{opt.label}</span>
                    <div
                      className={`h-5 w-5 rounded-full grid place-items-center text-xs transition-colors ${
                        selected ? "bg-gold text-primary font-bold" : "border border-border text-transparent"
                      }`}
                    >
                      <Check className="h-3 w-3 stroke-[3]" />
                    </div>
                  </div>
                  <span className="text-[0.7rem] text-muted-foreground mt-2">{opt.desc}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Password & Security Section */}
        <div className="bg-card border border-border/80 rounded-[20px] p-6 sm:p-8 shadow-luxe space-y-6">
          <div className="flex items-center gap-3 border-b border-border/60 pb-4">
            <div className="h-10 w-10 rounded-full bg-gold/10 grid place-items-center text-gold">
              <KeyRound className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold text-primary">Security & Password</h3>
              <p className="text-xs text-muted-foreground">Update your account login password.</p>
            </div>
          </div>

          <form onSubmit={handleUpdatePassword} className="space-y-5 max-w-md">
            <label className="block space-y-2">
              <span className="text-[0.65rem] font-semibold tracking-[0.25em] uppercase text-muted-foreground">New Password</span>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                className="w-full h-12 px-4 bg-background border border-border rounded-[12px] text-sm text-primary focus:outline-none focus:border-gold transition-colors"
              />
            </label>

            <label className="block space-y-2">
              <span className="text-[0.65rem] font-semibold tracking-[0.25em] uppercase text-muted-foreground">Confirm New Password</span>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                className="w-full h-12 px-4 bg-background border border-border rounded-[12px] text-sm text-primary focus:outline-none focus:border-gold transition-colors"
              />
            </label>

            <button
              type="submit"
              disabled={updatingPassword}
              className="btn-outline-dark inline-flex items-center gap-2 px-6 h-11 text-xs uppercase tracking-widest font-semibold"
            >
              <Shield className="h-4 w-4" />
              {updatingPassword ? "Updating Password..." : "Update Password"}
            </button>
          </form>
        </div>

      </div>
    </AccountLayout>
  );
}
